import { Fragment, useEffect, useMemo, useState } from "react";
import { DataTablePagination, paginationSplit } from "@/src/components/ui/data-table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/src/components/ui/pagination";
import { Amenity, Yacht } from "../../Yachts/model";
import { Button } from "@/src/components/ui/button";
import { CircleX } from "lucide-react";
import { CalendarView } from "./CalendarView";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/src/components/ui/dialog";
import { ActionButton } from "@/src/components/wrappers/WithActionBar";
import { YachtsFiltersForm } from "./YachtsFiltersForm/YachtsFiltersForm";
import { useEmit } from "@/src/lib/actions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useOffersStore } from "@/src/stores/offersStore";
import { emptyYachtsFiltersForm } from "./YachtsFiltersForm/yachts-filters.schema";
import { YachtCard } from "./YachtCard";
import { YachtDetailsModal } from "./YachtDetailsModal";
import { useFetch } from "@/src/lib/useFetch";
import { calculateCurrencyPrice } from "@/src/lib/utils";
import qs from "qs";
import { useFormContext } from "react-hook-form";
import { OfferForm } from "./offer.schema";
import { boatsFetch } from "../../../lib/boatsFetch";
import { useCharterFlags, useYachtCommissions } from "./CharterHistory";

export type YachtAmenities = {
  id: string;
  items: Amenity[];
}[];

type YachtsTable = {
  pagination: DataTablePagination;
  onPageClick?: (page: number) => void;
  items: Yacht[];
  openChangeDetails: (id: string) => void;
  onQuickAdd?: (id: string, fallback?: { name?: string; media?: string[]; slug?: string }) => void;
  viewMode?: "list" | "calendar";
  onAddWithDates?: (id: string, from: string, to: string) => void;
};
export type APIFilters = {
  priceMin: number;
  priceMax: number;
  cabinsMin: number;
  cabinsMax: number;
  yearMin: number;
  yearMax: number;
  crewMin: number;
  crewMax: number;
  bathroomsMin: number;
  bathroomsMax: number;
  lengthMin: number;
  lengthMax: number;
  ratingMin: number;
  ratingMax: number;
};
export const YachtsListing = ({
  pagination,
  onPageClick,
  items,
  openChangeDetails,
  onQuickAdd,
  viewMode = "list",
  onAddWithDates,
}: YachtsTable) => {
  const [amenities, setAmenities] = useState<YachtAmenities>([]);
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [yachtDetailsId, setYachtDetailsId] = useState<null | string>(null);
  const [apiFilters, setApiFilters] = useState<APIFilters | undefined>(undefined);
  const { watch } = useFormContext<OfferForm>();

  const offerCurrencyCode = watch("description.currency");

  const { fetch } = useFetch();

  const fetchAPIFilters = () => boatsFetch<APIFilters>(`/filters?target=ha`).then((res) => setApiFilters(res));

  const fetchAmenities = () => {
    try {
      fetch<{ data: YachtAmenities }>(
        "/api/amenities/list?" +
          qs.stringify(
            { ids: items?.map((i) => i.id) },
            {
              indices: false,
            },
          ),
      ).then((r) => {
        setAmenities(r.data);
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    !!items?.length && fetchAmenities();
  }, [items]);
  useEffect(() => {
    fetchAPIFilters();
  }, []);
  const { yachtsFilters, setYachtsFilters, currencies } = useOffersStore();
  const charterFlags = useCharterFlags(items?.map((i) => i.id) ?? []);
  const commissions = useYachtCommissions(items?.map((i) => i.id) ?? []);
  // Quick filter (prototype chiprow): only fleets with charter history.
  const [charteredOnly, setCharteredOnly] = useState(false);
  const [goodSuppliersOnly, setGoodSuppliersOnly] = useState(false);

  // Inline quick filters (name / operator / price) — debounced writes into the
  // shared yachtsFilters so the server re-searches. Yacht-name & operator are
  // now case-insensitive substring matches server-side.
  const [nameQ, setNameQ] = useState<string>((yachtsFilters as any)?.yachtName?.[0] ?? "");
  const [opQ, setOpQ] = useState<string>((yachtsFilters as any)?.operator?.[0] ?? "");
  const [priceMinQ, setPriceMinQ] = useState<string>(
    (yachtsFilters as any)?.priceMin ? String((yachtsFilters as any).priceMin) : "",
  );
  const [priceMaxQ, setPriceMaxQ] = useState<string>(
    (yachtsFilters as any)?.priceMax ? String((yachtsFilters as any).priceMax) : "",
  );
  useEffect(() => {
    const t = setTimeout(() => {
      const cur: any = { ...(useOffersStore.getState().yachtsFilters ?? {}) };
      // The broker types prices in the offer's display currency, but the search
      // filters on the base (EUR) price — convert so the numbers line up.
      const eur = currencies?.find((c) => c.code === "EUR");
      const disp = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));
      const toEur = (n: number) => (eur && disp && disp.code !== "EUR" ? calculateCurrencyPrice(eur, disp, n) : n);
      if (nameQ.trim()) cur.yachtName = [nameQ.trim()];
      else delete cur.yachtName;
      // operator is a string[] in the filter schema (FilterPills maps over it);
      // must be an array, not a bare string, or .map crashes.
      if (opQ.trim()) cur.operator = [opQ.trim()];
      else delete cur.operator;
      if (priceMinQ && Number(priceMinQ) > 0) cur.priceMin = Math.round(toEur(Number(priceMinQ)));
      else delete cur.priceMin;
      if (priceMaxQ && Number(priceMaxQ) > 0) cur.priceMax = Math.round(toEur(Number(priceMaxQ)));
      else delete cur.priceMax;
      setYachtsFilters(cur);
    }, 400);
    return () => clearTimeout(t);
  }, [nameQ, opQ, priceMinQ, priceMaxQ, offerCurrencyCode, currencies]);
  // CYA / Yachtfolio is a SERVER-side provider filter (not a client-side narrow of the
  // current page) so the matches are paginated from page 1 and come into view, rather
  // than staying scattered behind whatever page you were on.
  const CREWED_PROVIDERS = ["CYA", "Yachtfolio"] as const;
  const crewedProviderActive =
    (yachtsFilters?.provider?.length ?? 0) === CREWED_PROVIDERS.length &&
    CREWED_PROVIDERS.every((p) => (yachtsFilters?.provider ?? []).includes(p));
  const toggleCrewedProvider = () => {
    const next: any = { ...(yachtsFilters ?? {}) };
    if (crewedProviderActive) delete next.provider;
    else next.provider = [...CREWED_PROVIDERS];
    setYachtsFilters(next);
  };

  // Generator + A/C uses the SAME server-side amenity filter as the Filters
  // modal (AND semantics: a yacht must have every selected amenity), rather
  // than client-side text matching on the batched amenity list.
  const [amenityGroupList, setAmenityGroupList] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    fetch<{ data: { id: string; name: string }[] }>("/api/amenities/groups")
      .then((r) => setAmenityGroupList(r.data ?? []))
      .catch(() => {});
  }, []);
  const genAcIds = useMemo(() => {
    const find = (pred: (n: string) => boolean) =>
      amenityGroupList.find((g) => pred((g.name ?? "").toLowerCase()))?.id;
    const gen = find((n) => n.includes("generator"));
    const ac = find((n) => n.includes("air con") || n.includes("air-con") || n.includes("aircon") || n === "a/c");
    return [gen, ac].filter(Boolean) as string[];
  }, [amenityGroupList]);
  const genAcActive = genAcIds.length === 2 && genAcIds.every((id) => yachtsFilters?.amenities?.includes(id));
  const toggleGenAc = () => {
    const current = new Set(yachtsFilters?.amenities ?? []);
    if (genAcActive) genAcIds.forEach((id) => current.delete(id));
    else genAcIds.forEach((id) => current.add(id));
    const amenities = Array.from(current);
    const next: any = { ...(yachtsFilters ?? {}) };
    if (amenities.length) next.amenities = amenities;
    else delete next.amenities;
    setYachtsFilters(next);
  };

  const visibleItems = (items ?? [])
    .filter((i) => !charteredOnly || !!charterFlags[i.id]?.count)
    .filter((i) => !goodSuppliersOnly || Number(i.operatorRating) >= 6);

  const pSplit = pagination && !!pagination?.total ? paginationSplit(pagination) : null;

  const enableClearAll = useMemo(() => {
    // Remove search filters to leave only the modal filters
    // might be good to refactor - split filters into modal and search related
    const modalFilters = { ...yachtsFilters };
    delete modalFilters?.checkIn;
    delete modalFilters?.checkOut;
    delete modalFilters?.country;
    delete modalFilters?.location;
    delete modalFilters?.region;
    delete modalFilters?.boatType;
    delete modalFilters?.region;
    delete modalFilters?.people;

    return modalFilters && !!Object.keys(modalFilters)?.length;
  }, [yachtsFilters]);

  const openYachtDetails = (id: string) => {
    setDetailsOpen(true);
    setYachtDetailsId(id);
  };

  const tripDuration =
    yachtsFilters?.checkIn && yachtsFilters?.checkOut
      ? Math.ceil(
          (new Date(yachtsFilters.checkOut).valueOf() - new Date(yachtsFilters.checkIn).valueOf()) /
            (1000 * 60 * 60 * 24),
        )
      : undefined;

  return (
    <div>
      <div className="flex items-center mb-4">
        <h3 className="text-xl mr-auto">Results</h3>
        <PageSizeSelect />
        <SortingSelect />
        <Dialog onOpenChange={(v) => setVisible(v)} open={visible}>
          <DialogTrigger asChild>
            <Button className="bg-white" variant="outline">
              Filters
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-3xl">Filters</DialogTitle>
            </DialogHeader>
            {apiFilters && <YachtsFiltersForm apiFilters={apiFilters} onSave={() => setVisible(false)} />}
            <DialogFooter>
              <ActionButton disabled={!enableClearAll} variant="link" className="mr-auto" eventKey="filters:clear">
                Clear all
              </ActionButton>
              <ActionButton eventKey="filters:save" type="submit">
                Show yachts
              </ActionButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <FilterPills />
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">Yacht name</label>
          <input
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            placeholder="e.g. blue"
            className="border rounded-md px-2 py-1.5 text-sm border-slate-200 w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">Operator</label>
          <input
            value={opQ}
            onChange={(e) => setOpQ(e.target.value)}
            placeholder="e.g. dream yacht"
            className="border rounded-md px-2 py-1.5 text-sm border-slate-200 w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">Price /wk ({offerCurrencyCode ?? "EUR"})</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={priceMinQ}
              onChange={(e) => setPriceMinQ(e.target.value)}
              placeholder="min"
              className="border rounded-md px-2 py-1.5 text-sm border-slate-200 w-24"
            />
            <span className="text-slate-400">–</span>
            <input
              type="number"
              value={priceMaxQ}
              onChange={(e) => setPriceMaxQ(e.target.value)}
              placeholder="max"
              className="border rounded-md px-2 py-1.5 text-sm border-slate-200 w-24"
            />
          </div>
        </div>
        {(nameQ || opQ || priceMinQ || priceMaxQ) && (
          <button
            type="button"
            onClick={() => {
              setNameQ("");
              setOpQ("");
              setPriceMinQ("");
              setPriceMaxQ("");
            }}
            className="text-xs text-slate-500 hover:text-slate-700 underline mb-2"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs text-slate-500">Quick filters:</span>
        <button
          type="button"
          onClick={() => setCharteredOnly((v) => !v)}
          className={`text-sm rounded-full border px-3 py-1 ${
            charteredOnly ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
          }`}
        >
          ❤ Chartered before
        </button>
        <button
          type="button"
          onClick={() => setGoodSuppliersOnly((v) => !v)}
          className={`text-sm rounded-full border px-3 py-1 ${
            goodSuppliersOnly ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
          }`}
          title="Operators rated 6/10 and above"
        >
          ★ Good suppliers
        </button>
        <button
          type="button"
          onClick={toggleGenAc}
          disabled={genAcIds.length < 2}
          className={`text-sm rounded-full border px-3 py-1 disabled:opacity-40 ${
            genAcActive ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
          }`}
          title="Yachts with both a generator and air conditioning"
        >
          Generator + A/C
        </button>
        <button
          type="button"
          onClick={toggleCrewedProvider}
          className={`text-sm rounded-full border px-3 py-1 ${
            crewedProviderActive ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
          }`}
          title="CYA and Yachtfolio (crewed) yachts only"
        >
          CYA / Yachtfolio
        </button>
        {/* Year-from filter: `yearMin` maps to the search's `year_low` (Year >= n). */}
        <select
          value={(yachtsFilters as any)?.yearMin ?? ""}
          onChange={(e) => {
            const next: any = { ...(yachtsFilters ?? {}) };
            if (e.target.value) next.yearMin = Number(e.target.value);
            else delete next.yearMin;
            setYachtsFilters(next);
          }}
          className={`text-sm rounded-full border px-3 py-1 ${
            (yachtsFilters as any)?.yearMin
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-slate-600 border-slate-200"
          }`}
          title="Only yachts built in this year or later"
        >
          <option value="">Year from</option>
          {Array.from({ length: new Date().getFullYear() - 1989 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-y-4">
        {viewMode === "calendar" ? (
          <CalendarView
            items={visibleItems as any}
            openChangeDetails={openChangeDetails}
            onAddWithDates={onAddWithDates}
          />
        ) : !!visibleItems?.length ? (
          visibleItems?.map((item, i) => (
            <YachtCard
              key={i}
              {...item}
              showBreakdown={!!yachtsFilters?.checkIn}
              tripLength={tripDuration}
              amenities={amenities?.find((am) => am.id === item.id)?.items ?? []}
              openYachtDetails={() => openYachtDetails(item.id)}
              openChangeDetails={() => openChangeDetails(item.id)}
              onQuickAdd={
                onQuickAdd
                  ? () => onQuickAdd(item.id, { name: item.name, media: item.media, slug: item.slug })
                  : undefined
              }
              offerCurrencyCode={offerCurrencyCode}
              charterFlag={charterFlags[item.id]}
              commission={commissions[item.id]}
            />
          ))
        ) : (
          <>
            <h4 className="text-xl text-center">No results</h4>
            <p className="text-base text-center">
              Try changing or removing some of your filters or choose different dates.
            </p>
          </>
        )}
      </div>
      {pSplit && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  onPageClick?.(pSplit.currentPage - 1);
                }}
                disabled={pSplit.currentPage === 1}
              />
            </PaginationItem>
            {pSplit.segments.map((s, i) => (
              <Fragment key={i}>
                {i > 0 && <PaginationEllipsis />}
                {s.map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={pSplit.currentPage === page}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageClick?.(page);
                      }}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
              </Fragment>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  onPageClick?.(pSplit.currentPage + 1);
                }}
                disabled={pSplit.currentPage === pSplit.lastPage}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      {detailsOpen && <YachtDetailsModal yachtDetailsId={yachtDetailsId} open={detailsOpen} setOpen={setDetailsOpen} />}
    </div>
  );
};

const FilterPills = () => {
  const { yachtsFilters, setYachtsFilters, amenityGroups } = useOffersStore();

  if (yachtsFilters && !Object.keys(yachtsFilters)?.length) {
    return null;
  }
  const resetFilter = (filterName: keyof typeof emptyYachtsFiltersForm) => {
    const filters = { ...yachtsFilters };
    delete filters[filterName];
    setYachtsFilters(filters);
  };

  const resetCharterType = (type: string) => {
    const charterType = yachtsFilters?.charterType?.filter((charter) => type !== charter);
    const filters = { ...yachtsFilters };
    if (!!charterType?.length) {
      filters["charterType"] = charterType;
    } else {
      delete filters["charterType"];
    }
    setYachtsFilters(filters);
  };
  const resetBrand = (type: string) => {
    const brand = yachtsFilters?.brand?.filter((charter) => type !== charter);
    const filters = { ...yachtsFilters };
    if (!!brand?.length) {
      filters["brand"] = brand;
    } else {
      delete filters["brand"];
    }
    setYachtsFilters(filters);
  };

  const resetProvider = (type: string) => {
    const provider = yachtsFilters?.provider?.filter((prov) => type !== prov);

    const filters = { ...yachtsFilters };
    if (!!provider?.length) {
      filters["provider"] = provider;
    } else {
      delete filters["provider"];
    }
    setYachtsFilters(filters);
  };

  const resetAmenities = (amName: string) => {
    const amenity = yachtsFilters?.amenities?.filter((a) => amName !== a);

    const filters = { ...yachtsFilters };
    if (!!amenity?.length) {
      filters["amenities"] = amenity;
    } else {
      delete filters["amenities"];
    }
    setYachtsFilters(filters);
  };

  const resetOperator = (op: string) => {
    const operator = yachtsFilters?.operator?.filter((o) => o !== op);

    const filters = { ...yachtsFilters };
    if (!!operator?.length) {
      filters["operator"] = operator;
    } else {
      delete filters["operator"];
    }
    setYachtsFilters(filters);
  };

  const resetYachtName = (name: string) => {
    const names = yachtsFilters?.yachtName?.filter((n) => n !== name);

    const filters = { ...yachtsFilters };
    if (!!names?.length) {
      filters["yachtName"] = names;
    } else {
      delete filters["yachtName"];
    }
    setYachtsFilters(filters);
  };

  return (
    <div className="flex flex-wrap gap-4 mb-4">
      {yachtsFilters?.brand?.map((type) => (
        <FilterPill key={type} text={type} onClick={() => resetBrand(type.toLowerCase())} />
      ))}
      {yachtsFilters?.charterType?.map((type) => (
        <FilterPill key={type} text={type} onClick={() => resetCharterType(type.toLowerCase())} />
      ))}
      {yachtsFilters?.provider?.map((provider) => (
        <FilterPill key={provider} text={provider} onClick={() => resetProvider(provider)} />
      ))}
      {yachtsFilters?.cabinsMin && (
        <FilterPill text={`Min cabins ${yachtsFilters?.cabinsMin}`} onClick={() => resetFilter("cabinsMin")} />
      )}
      {yachtsFilters?.cabinsMax && (
        <FilterPill text={`Max cabins ${yachtsFilters?.cabinsMax}`} onClick={() => resetFilter("cabinsMax")} />
      )}
      {yachtsFilters?.bathroomsMin && (
        <FilterPill text={`Min bathrooms ${yachtsFilters?.bathroomsMin}`} onClick={() => resetFilter("bathroomsMin")} />
      )}
      {yachtsFilters?.bathroomsMax && (
        <FilterPill text={`Max bathrooms ${yachtsFilters?.bathroomsMax}`} onClick={() => resetFilter("bathroomsMax")} />
      )}
      {yachtsFilters?.yearMin && (
        <FilterPill text={`Min year ${yachtsFilters?.yearMin}`} onClick={() => resetFilter("yearMin")} />
      )}
      {yachtsFilters?.yearMax && (
        <FilterPill text={`Max year ${yachtsFilters?.yearMax}`} onClick={() => resetFilter("yearMax")} />
      )}
      {yachtsFilters?.lengthMin && (
        <FilterPill text={`Min length ${yachtsFilters?.lengthMin}`} onClick={() => resetFilter("lengthMin")} />
      )}
      {yachtsFilters?.lengthMax && (
        <FilterPill text={`Max length ${yachtsFilters?.lengthMax}`} onClick={() => resetFilter("lengthMax")} />
      )}
      {yachtsFilters?.crewMin && (
        <FilterPill text={`Min crew members ${yachtsFilters?.crewMin}`} onClick={() => resetFilter("crewMin")} />
      )}
      {yachtsFilters?.crewMax && (
        <FilterPill text={`Max crew members ${yachtsFilters?.crewMax}`} onClick={() => resetFilter("crewMax")} />
      )}
      {yachtsFilters?.ratingMin && (
        <FilterPill text={`Min rating ${yachtsFilters?.ratingMin}`} onClick={() => resetFilter("ratingMin")} />
      )}
      {yachtsFilters?.ratingMax && (
        <FilterPill text={`Max rating ${yachtsFilters?.ratingMax}`} onClick={() => resetFilter("ratingMax")} />
      )}
      {!!yachtsFilters?.amenities?.length &&
        yachtsFilters.amenities?.map((a) => (
          <FilterPill text={amenityGroups.find((am) => am.id === a)?.name ?? ""} onClick={() => resetAmenities(a)} />
        ))}
      {Array.isArray(yachtsFilters?.operator) &&
        yachtsFilters.operator.map((op) => <FilterPill text={op} onClick={() => resetOperator(op)} />)}
      {Array.isArray(yachtsFilters?.yachtName) &&
        yachtsFilters.yachtName?.map((name) => <FilterPill text={name} onClick={() => resetYachtName(name)} />)}
    </div>
  );
};

const FilterPill = ({ onClick, text }: { onClick: () => void; text: string }) => {
  return (
    <span
      onClick={onClick}
      className="py-2 px-4 border border-blue-100 rounded-[6px] flex items-center text-sm gap-2 capitalize"
    >
      {text}
      <CircleX size={16} />
    </span>
  );
};

function PageSizeSelect() {
  const [value, setValue] = useState("40");
  const emit = useEmit<number>("ych:page-size");
  const pageChangeEmit = useEmit<number>("ych:page");

  const onValueChange = (value: string) => {
    pageChangeEmit(1);
    setValue(value);
    emit(parseInt(value));
  };

  return (
    <div className="w-40 mr-4">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">Show: 10 at once</SelectItem>
          <SelectItem value="20">Show: 20 at once</SelectItem>
          <SelectItem value="40">Show: 40 at once</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function SortingSelect() {
  const [value, setValue] = useState("priceASC");
  const emit = useEmit<string>("ych:sorting");

  const onValueChange = (value: string) => {
    setValue(value);
    emit(value);
  };

  return (
    <div className="w-60 mr-4">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="priceASC">Sort by: Price/week ascending</SelectItem>
          <SelectItem value="priceDESC">Sort by: Price/week descending</SelectItem>
          <SelectItem value="lengthASC">Length: low to high</SelectItem>
          <SelectItem value="lengthDESC">Length: high to low</SelectItem>
          <SelectItem value="discountDESC">Sort by: Discount</SelectItem>
          <SelectItem value="recommended">HELM recommended</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
